import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTicket, transitionTicket, signoffTicket } from '../../store/slices/ticketsSlice';
import { fetchComments, postComment, updateComment, deleteComment } from '../../store/slices/commentsSlice';
import { fetchEscalations } from '../../store/slices/escalationsSlice';
import StatusBadge from '../../components/StatusBadge';
import TransitionButton from '../../components/TransitionButton';
import NBDTimer from '../../components/NBDTimer';
import RMAPanel from './RMAPanel';
import client from '../../api/client';


const INCIDENT_TRANSITIONS = {
  'Open': ['Ticket Assignment', 'Troubleshooting'],
  'Ticket Assignment': ['Troubleshooting'],
  'Troubleshooting': ['Fault Confirmation'],
  'Fault Confirmation': ['RMA', 'Resolved'],
  // 'On Hold' is no longer an option you can move a ticket INTO (removed per
  // request) — but any ticket already sitting in that status from before
  // needs a way back out, otherwise it's permanently stuck with no
  // transition button and therefore no audit log entry either.
  'On Hold': ['Troubleshooting'],
  'RMA': ['Replacement'],
  'Replacement': ['Resolved'],
  'Resolved': ['Recovery', 'Closed'],
  'Recovery': ['Closed'],
  'Closed': [],
};

const SR_TRANSITIONS = {
  'Open': ['Ticket Assignment'],
  'Ticket Assignment': ['Configuration and Installation'],
  'Configuration and Installation': ['Closed'],
  'Closed': [],
};

const PM_TRANSITIONS = {
  'Open': ['Ticket Assignment'],
  'Ticket Assignment': ['Maintenance'],
  'Maintenance': ['Resolved'],
  'Resolved': ['Closed'],
  'Closed': [],
};

const AM_TRANSITIONS = {
  'Open': ['Ticket Assignment'],
  'Ticket Assignment': ['Asset Review'],
  'Asset Review': ['Resolved'],
  'Resolved': ['Closed'],
  'Closed': [],
};

const getTransitions = (ticket) => {
  let options;
  if (ticket?.sub_category === 'SERVICE_REQUEST') options = SR_TRANSITIONS[ticket.status] || [];
  else if (ticket?.sub_category === 'PREVENTIVE_MAINTENANCE') options = PM_TRANSITIONS[ticket.status] || [];
  else if (ticket?.sub_category === 'ASSET_MANAGEMENT') options = AM_TRANSITIONS[ticket.status] || [];
  else options = INCIDENT_TRANSITIONS[ticket.status] || [];

  // Mirror backend: skip Ticket Assignment if ticket already has an owner
  if (ticket?.status === 'Open' && ticket?.assigned_to) {
    const skipTo = {
      'SERVICE_REQUEST':        'Configuration and Installation',
      'PREVENTIVE_MAINTENANCE': 'Maintenance',
      'ASSET_MANAGEMENT':       'Asset Review',
    }[ticket.sub_category] || 'Troubleshooting';
    const seen = new Set();
    options = options.map(t => t === 'Ticket Assignment' ? skipTo : t).filter(t => !seen.has(t) && seen.add(t));
  }

  // RMA and Recovery only make sense for a hardware fault
  if (ticket?.fault_type === 'SOFTWARE') {
    options = options.filter(t => t !== 'RMA' && t !== 'Recovery');
  }

  return options;
};

const WARNING_MINUTES = 20;
const BREACH_MINUTES  = 30;

function EscalationCountdown({ createdAt, status }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!createdAt) return;
    const tick = () => {
      const diff = (Date.now() - new Date(createdAt).getTime()) / 1000 / 60;
      setElapsed(diff);
    };
    tick();
    const id = setInterval(tick, 10000);
    return () => clearInterval(id);
  }, [createdAt]);

  const isOpen    = status === 'Open';
  const warned    = elapsed >= WARNING_MINUTES;
  const breached  = elapsed >= BREACH_MINUTES;
  const remaining = Math.max(0, BREACH_MINUTES - elapsed);
  const pct       = Math.min(100, (elapsed / BREACH_MINUTES) * 100);

  const fmt = (m) => {
    const mm = Math.floor(m);
    const ss = Math.floor((m - mm) * 60);
    return `${mm}m ${ss.toString().padStart(2, '0')}s`;
  };

  let barColor, borderColor, labelText, labelColor, bgColor;
  if (!isOpen) {
    barColor = 'bg-gray-300'; borderColor = 'border-gray-200';
    bgColor = 'bg-gray-50'; labelColor = 'text-gray-500';
    labelText = 'Escalation clock stopped — ticket left Open status';
  } else if (breached) {
    barColor = 'bg-red-500 animate-pulse'; borderColor = 'border-red-200';
    bgColor = 'bg-red-50'; labelColor = 'text-red-700';
    labelText = `SLA BREACHED — open for ${fmt(elapsed)}`;
  } else if (warned) {
    barColor = 'bg-amber-400'; borderColor = 'border-amber-200';
    bgColor = 'bg-amber-50'; labelColor = 'text-amber-700';
    labelText = `WARNING — ${fmt(remaining)} until breach`;
  } else {
    barColor = 'bg-emerald-400'; borderColor = 'border-emerald-200';
    bgColor = 'bg-emerald-50'; labelColor = 'text-emerald-700';
    labelText = `${fmt(remaining)} until SLA breach`;
  }

  return (
    <div className={`card p-4 border ${borderColor} ${bgColor}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Internal Escalation Timer</span>
        <span className={`text-xs font-bold ${labelColor}`}>{labelText}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
        <div className={`h-2.5 rounded-full transition-all duration-1000 ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="flex justify-between mt-1.5 text-xs text-gray-400">
        <span>Created</span>
        <span className={warned && isOpen ? 'text-amber-500 font-semibold' : ''}>⚠ 20m warning</span>
        <span className={breached && isOpen ? 'text-red-500 font-semibold' : ''}>🔴 30m breach</span>
      </div>
      <div className="flex gap-4 mt-3 text-xs text-gray-500">
        <span>Elapsed since creation: <strong className={labelColor}>{fmt(elapsed)}</strong></span>
      </div>
    </div>
  );
}

const ENTITLEMENT_FIELDS = [
  { name: 'start_date',        label: 'Contract / AMC Start Date', type: 'date' },
  { name: 'end_date',          label: 'Contract / AMC End Date',   type: 'date' },
  { name: 'oem',               label: 'OEM',                       type: 'text' },
  { name: 'part_number',       label: 'Part Number',               type: 'text' },
  { name: 'installation_date', label: 'Installation Date',         type: 'date' },
  { name: 'device_location',   label: 'Device Location (City)',    type: 'text' },
  { name: 'building_name',     label: 'Building Name',              type: 'text' },
  { name: 'room_name',         label: 'Room Name',                 type: 'text' },
  { name: 'room_class',        label: 'Room Class (SLA)',          type: 'text' },
];

// Keys must match the status names used in VALID_TRANSITIONS.
// Field names must match TicketFields model field names exactly.
const TRANSITION_FIELDS = {
  'Ticket Assignment': [
    { name: 'assigned_to', label: 'Assign To', type: 'agent_select', required: true },
  ],
  'Troubleshooting': [
    { name: 'next_action', label: 'Next Action', type: 'textarea', required: true },
    { name: 'l2_sme', label: 'L2 / SME Name', type: 'select', required: false,
      options: [
        { value: '', label: '— Select Engineer —' },
        { value: 'Keshav Singh', label: 'Keshav Singh' },
        { value: 'Sandeep Kumar Chaudhary', label: 'Sandeep Kumar Chaudhary' },
        { value: 'Rohit Mourya', label: 'Rohit Mourya' },
        { value: 'Shresth Kushwaha', label: 'Shresth Kushwaha' },
      ]},
    { name: 'raise_case_with_oem', label: '{OEM} Service Request Raised', type: 'checkbox', required: false },
    { name: 'oem_sr_number',  label: '{OEM} SR Number',       type: 'text',     required: false, showWhen: { field: 'raise_case_with_oem', value: true } },
    { name: 'sr_created_on',  label: '{OEM} SR Created On',   type: 'date',     required: false, showWhen: { field: 'raise_case_with_oem', value: true } },
    { name: 'reason_oem_case', label: 'Reason for {OEM} Case', type: 'textarea', required: false, showWhen: { field: 'raise_case_with_oem', value: true } },
  ],
  'Fault Confirmation': [
    { name: 'fault_type', label: 'Confirm Fault Type', type: 'select', required: true,
      options: [
        { value: 'HARDWARE', label: 'Hardware' },
        { value: 'SOFTWARE', label: 'Software' },
      ]},
  ],
  'RMA': [
    { name: 'rma_from', label: 'RMA From', type: 'select', required: true,
      options: [
        { value: '',                  label: '— Select —' },
        { value: 'From OEM',          label: 'From OEM' },
        { value: 'From Spares',       label: 'From Spares' },
        { value: 'From Another Room', label: 'From Another Room' },
      ]},
    { name: 'oem_rma_number',       label: '{OEM} RMA Number',        type: 'text',            required: false, showWhen: { field: 'rma_from', value: 'From OEM' } },
    { name: 'rma_room_serial',      label: 'Select Serial Number',    type: 'sibling_serial',  required: false, showWhen: { field: 'rma_from', value: 'From Another Room' } },
    { name: 'spare_warehouse',      label: 'Warehouse',               type: 'spare_warehouse',    required: false, showWhen: { field: 'rma_from', value: 'From Spares' } },
    { name: 'spare_unit_id',        label: 'Select Spare Unit',       type: 'spare_unit_select',  required: false, showWhen: { field: 'rma_from', value: 'From Spares' } },
    { name: 'oem_rma_number',       label: '{OEM} RMA Number',        type: 'text',               required: false, showWhen: { field: 'rma_from', value: 'From Spares' } },
    { name: 'rma_created_on',       label: 'RMA Created On',          type: 'date',            required: false },
    { name: 'next_action_rma',      label: 'Next Action',             type: 'textarea',        required: false },
  ],
  'Replacement': [
    { name: 'replaced_part_number',     label: 'Replacement Part Number',   type: 'text',     required: false },
    { name: 'replaced_serial_number',   label: 'Replacement Serial Number', type: 'text',     required: false },
    { name: 'replacement_completed_on', label: 'Replacement Completed On',  type: 'date',     required: false },
    { name: 'next_action_replacement',  label: 'Next Action',               type: 'textarea', required: false },
  ],
  'Recovery': [
    { name: 'picked_serial_number',      label: 'Faulty Part Serial Number', type: 'text',     required: false },
    { name: 'faulty_part_actual_pickup', label: 'Actual Pickup Date',        type: 'date',     required: false },
    { name: 'notes',                     label: 'Additional Notes',          type: 'textarea', required: false },
  ],
  'Configuration and Installation': [
    { name: 'l2_sme', label: 'L2 / SME Name', type: 'select', required: false,
      options: [
        { value: '', label: '— Select Engineer —' },
        { value: 'Keshav Singh', label: 'Keshav Singh' },
        { value: 'Sandeep Kumar Chaudhary', label: 'Sandeep Kumar Chaudhary' },
        { value: 'Rohit Mourya', label: 'Rohit Mourya' },
        { value: 'Shresth Kushwaha', label: 'Shresth Kushwaha' },
      ]},
    { name: 'raise_case_with_oem', label: '{OEM} Service Request Raised', type: 'checkbox', required: false },
    { name: 'oem_sr_number', label: '{OEM} SR Number',    type: 'text', required: false, showWhen: { field: 'raise_case_with_oem', value: true } },
    { name: 'sr_created_on', label: '{OEM} SR Created On', type: 'date', required: false, showWhen: { field: 'raise_case_with_oem', value: true } },
    { name: 'notes',         label: 'Notes',              type: 'textarea', required: false },
  ],
  'Maintenance': [
    { name: 'pm_report', label: 'PM Report (Excel / PDF)', type: 'file', required: true },
    { name: 'pm_notes',  label: 'Maintenance Notes',       type: 'textarea', required: false },
  ],
  'Asset Review': [
    { name: 'pm_notes', label: 'Asset Review Notes', type: 'textarea', required: false },
    { name: 'notes',    label: 'Additional Notes',   type: 'textarea', required: false },
  ],
  'Resolved': [
    { name: 'resolution', label: 'Resolution Summary', type: 'textarea',       required: true  },
    { name: 'resolved_on', label: 'Resolved On',       type: 'datetime-local', required: false },
    { name: 'notes',       label: 'Additional Notes',  type: 'textarea',       required: false },
  ],
  'Closed': [
    { name: 'resolution', label: 'Resolution Summary', type: 'textarea',       required: true  },
    { name: 'resolved_on', label: 'Closed On',         type: 'datetime-local', required: false },
    { name: 'notes',       label: 'Additional Notes',  type: 'textarea',       required: false },
  ],
};

function FieldInput({ field, value, onChange, agents = [], allUsers = [], siblingDevices = [], transitionFields = {} }) {
  if (field.type === 'checkbox') return (
    <label className="flex items-center gap-3 cursor-pointer group">
      <div className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-colors flex-shrink-0 ${value === true ? 'bg-magenta-500 border-magenta-500' : 'border-gray-300 group-hover:border-magenta-400'}`}
        onClick={() => onChange(!value)}>
        {value === true && (
          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>
      <span className="text-sm text-gray-700 select-none">Yes, SR has been raised</span>
    </label>
  );
  if (field.type === 'textarea') return (
    <textarea value={value} onChange={e => onChange(e.target.value)} rows={3}
      className="input-field resize-none" required={field.required} />
  );
  if (field.type === 'select') return (
    <select value={value} onChange={e => onChange(e.target.value)}
      className="input-field" required={field.required}>
      {(field.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
  if (field.type === 'sibling_serial') {
    if (siblingDevices.length === 0) return (
      <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
        No other {field._deviceName || 'devices'} found in this sub-location.
      </p>
    );
    return (
      <select value={value} onChange={e => onChange(e.target.value)} className="input-field" required={field.required}>
        <option value="">— Select serial number —</option>
        {siblingDevices.map(d => (
          <option key={d.id} value={d.serial_number}>
            {d.serial_number}{d.room?.room_name ? ` — ${d.room.room_name}` : ''}
          </option>
        ))}
      </select>
    );
  }
  if (field.type === 'spare_warehouse') {
    return (
      <select value={value} onChange={e => onChange(e.target.value)} className="input-field" required={field.required}>
        <option value="">— Select warehouse —</option>
        <option value="delhi">New Delhi</option>
        <option value="bangalore">Bangalore</option>
      </select>
    );
  }

  if (field.type === 'spare_unit_select') {
    const { spareUnits = [], spareUnitsLoading } = transitionFields._meta || {};
    if (!transitionFields.spare_warehouse) {
      return <p className="text-sm text-gray-400 italic">Select a warehouse first.</p>;
    }
    if (spareUnitsLoading) return <p className="text-sm text-gray-400 italic">Loading units…</p>;
    if (spareUnits.length === 0) return (
      <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
        No available spare units in this warehouse.
      </p>
    );
    return (
      <select value={value} onChange={e => onChange(e.target.value)} className="input-field" required={field.required}>
        <option value="">— Select spare unit —</option>
        {spareUnits.map(u => (
          <option key={u.id} value={u.id}>
            {u.device_name}{u.model ? ` — ${u.model}` : ''}{u.serial_number ? ` (S/N: ${u.serial_number})` : ''}
          </option>
        ))}
      </select>
    );
  }

  if (field.type === 'user_select' || field.type === 'agent_select') return (
    <select value={value} onChange={e => onChange(e.target.value)}
      className="input-field" required={field.required}>
      <option value="">— Select agent —</option>
      {(field.type === 'agent_select' ? allUsers : agents).map(a => (
        <option key={a.id} value={a.id}>
          {a.full_name} ({a.role})
        </option>
      ))}
    </select>
  );
  if (field.type === 'file') return (
    <div>
      <input
        type="file"
        accept=".xlsx,.xls,.pdf,.doc,.docx,.lpz,.json,.vtz,.sig"
        onChange={e => onChange(e.target.files?.[0] || null)}
        className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-brand-50 file:text-brand-600 hover:file:bg-brand-100 cursor-pointer"
        required={field.required}
      />
      <p className="text-xs text-gray-400 mt-1">Accepted: PDF, Word, Excel, LPZ, JSON, VTZ, SIG</p>
      {value && <p className="text-xs text-emerald-600 mt-1">✓ {typeof value === 'object' ? value.name : value}</p>}
    </div>
  );
  if (field.type === 'datetime-local') {
    const now = new Date();
    const pad = n => String(n).padStart(2, '0');
    const maxVal = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
    return (
      <input type="datetime-local" value={value} onChange={e => onChange(e.target.value)}
        max={maxVal} className="input-field" required={field.required} />
    );
  }
  return (
    <input type={field.type} value={value} onChange={e => onChange(e.target.value)}
      className="input-field" required={field.required} />
  );
}

export default function TicketDetail() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const ticket = useSelector(s => s.tickets.current);
  const user = useSelector(s => s.auth.user) || JSON.parse(localStorage.getItem('user') || '{}');

  const [transitionModal, setTransitionModal] = useState(null);
  const [transitionFields, setTransitionFields] = useState({});
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [l2Modal, setL2Modal] = useState(false);
  const [l2Users, setL2Users] = useState([]);
  const [l2Selected, setL2Selected] = useState('');
  const [l2Saving, setL2Saving] = useState(false);
  const [agents, setAgents] = useState([]);
  const [siblingDevices, setSiblingDevices] = useState([]);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignAgent, setReassignAgent] = useState('');
  const [rmaChild, setRmaChild] = useState(null);
  const [reassignLoading, setReassignLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const isClosed = ticket?.status === 'Closed';
  const [entitlementEdit, setEntitlementEdit] = useState(false);
  const [entitlementRefreshing, setEntitlementRefreshing] = useState(false);
  const [faultTypeSaving, setFaultTypeSaving] = useState(false);
  const [troubleshootingEdit, setTroubleshootingEdit] = useState(false);
  const [troubleshootingForm, setTroubleshootingForm] = useState({});
  const [troubleshootingSaving, setTroubleshootingSaving] = useState(false);
  const [entitlementForm, setEntitlementForm] = useState({});
  const [entitlementSaving, setEntitlementSaving] = useState(false);
  const [pmUploadFile, setPmUploadFile] = useState(null);
  const [pmUploading, setPmUploading] = useState(false);

  const comments = useSelector(s => s.comments.byTicket[ticket?.id] || []);
  const commentPosting = useSelector(s => s.comments.posting);
  const [commentText, setCommentText] = useState('');
  const [commentFiles, setCommentFiles] = useState([]);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionDropdown, setMentionDropdown] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const commentTextareaRef = useRef(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const startEditComment = (c) => {
    setEditingCommentId(c.id);
    setEditText(c.message);
  };
  const cancelEditComment = () => {
    setEditingCommentId(null);
    setEditText('');
  };
  const saveEditComment = async (commentId) => {
    if (!editText.trim()) return;
    setEditSaving(true);
    try {
      await dispatch(updateComment({ ticketId: ticket.id, commentId, message: editText.trim() })).unwrap();
      setEditingCommentId(null);
      setEditText('');
    } catch (e) {
      // error surfaced via rejected action; keep edit box open
    } finally {
      setEditSaving(false);
    }
  };
  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    await dispatch(deleteComment({ ticketId: ticket.id, commentId }));
  };

  const escalationsByOem = useSelector(s => s.escalations.byOem);
  const oem = ticket?.device_detail?.oem;
  const escalations = oem ? (escalationsByOem[oem] || []) : [];

  const [attachments, setAttachments] = useState([]);
  const [attachUploading, setAttachUploading] = useState(false);
  const [attachError, setAttachError] = useState('');

  // Mandatory fields config from backend
  const [mandatoryConfig, setMandatoryConfig] = useState([]);
  const [slaConfig, setSlaConfig] = useState({ warningMinutes: 20, breachMinutes: 30 });

  useEffect(() => {
    client.get('/config/mandatory-fields/').then(({ data }) => setMandatoryConfig(data)).catch(() => {});
    client.get('/config/escalation/').then(({ data }) => {
      setSlaConfig({
        warningMinutes: data.minutes_until_at_risk ?? 20,
        breachMinutes:  data.minutes_until_critical ?? 30,
      });
    }).catch(() => {});
  }, []);

  // Returns a Set of field names that are required for a given from→to transition
  const getRequiredFieldNames = (fromStatus, toStatus) => {
    const group = mandatoryConfig.find(g => g.from_status === fromStatus && g.to_status === toStatus);
    if (!group) return new Set();
    return new Set(group.fields.filter(f => f.is_required).map(f => f.field_name));
  };

  // Open-status inline edit
  const [openEdit, setOpenEdit] = useState(false);
  const [openForm, setOpenForm] = useState({ subject: '', description: '' });
  const [openSaving, setOpenSaving] = useState(false);
  const [deviceEdit, setDeviceEdit] = useState(false);
  const [deviceOptions, setDeviceOptions] = useState([]);
  const [deviceSearch, setDeviceSearch] = useState('');
  const [selectedDeviceId, setSelectedDeviceId] = useState('');

  useEffect(() => { dispatch(fetchTicket(id)); }, [dispatch, id]);

  useEffect(() => {
    if (ticket?.device_detail?.oem) {
      dispatch(fetchEscalations(ticket.device_detail.oem));
    }
  }, [dispatch, ticket?.device_detail?.oem]);

  const [vendorContacts, setVendorContacts] = useState([]);
  useEffect(() => {
    const oem = ticket?.device_detail?.oem;
    const state = ticket?.device_detail?.room?.location?.state;
    if (!oem) { setVendorContacts([]); return; }
    const params = new URLSearchParams({ oem });
    if (state) params.append('state', state);
    client.get(`/vendor-contacts/?${params}`).then(({ data }) => {
      setVendorContacts(Array.isArray(data) ? data : (data.results ?? []));
    }).catch(() => setVendorContacts([]));
  }, [ticket?.device_detail?.oem, ticket?.device_detail?.room?.location?.state]);

  // Fetch RE agents once
  useEffect(() => {
    client.get('/auth/users/?role=RE').then(({ data }) => {
      setAgents(data.results || data);
    }).catch(() => {});
  }, []);

  // Load all devices when device picker opens
  useEffect(() => {
    if (!deviceEdit) return;
    client.get('/inventory/').then(({ data }) => {
      setDeviceOptions(data.results || data);
    }).catch(() => {});
  }, [deviceEdit]);

  const saveOpenEdit = async () => {
    if (!openForm.subject.trim()) return;
    setOpenSaving(true);
    try {
      await client.patch(`/tickets/${ticket.id}/`, { subject: openForm.subject, description: openForm.description });
      dispatch(fetchTicket(id));
      setOpenEdit(false);
    } catch {} finally { setOpenSaving(false); }
  };

  const saveDeviceEdit = async () => {
    if (!selectedDeviceId) return;
    setOpenSaving(true);
    try {
      await client.patch(`/tickets/${ticket.id}/`, { device: selectedDeviceId });
      dispatch(fetchTicket(id));
      setDeviceEdit(false);
      setDeviceSearch('');
      setSelectedDeviceId('');
    } catch {} finally { setOpenSaving(false); }
  };

  useEffect(() => {
    if (ticket?.id) dispatch(fetchComments(ticket.id));
  }, [dispatch, ticket?.id]);

  useEffect(() => {
    if (ticket?.id) {
      client.get(`/tickets/${ticket.id}/attachments/`).then(({ data }) => setAttachments(data)).catch(() => {});
    }
  }, [ticket?.id]);

  const downloadFile = async (url, filename) => {
    try {
      // Force https to avoid the browser blocking the fetch as mixed content
      // if the backend ever returns an http:// URL while the site itself is https.
      const safeUrl = window.location.protocol === 'https:' ? url.replace(/^http:\/\//, 'https://') : url;
      const resp = await fetch(safeUrl);
      if (!resp.ok) throw new Error('Download failed');
      const blob = await resp.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 2000);
    } catch (err) {
      alert('Could not download the file. Please try again.');
    }
  };

  const handleAttachUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttachUploading(true);
    setAttachError('');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await client.post(`/tickets/${ticket.id}/attachments/`, fd);
      setAttachments(prev => [data, ...prev]);
    } catch { setAttachError('Upload failed. Please try again.'); }
    finally { setAttachUploading(false); e.target.value = ''; }
  };

  const handleAttachDelete = async (attachId) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await client.delete(`/tickets/${ticket.id}/attachments/${attachId}/`);
      setAttachments(prev => prev.filter(a => a.id !== attachId));
    } catch { setAttachError('Delete failed.'); }
  };

  // When warehouse is selected in RMA "From Spares", fetch available spare units
  useEffect(() => {
    if (transitionModal === 'RMA' && transitionFields.rma_from === 'From Spares' && transitionFields.spare_warehouse) {
      setTransitionFields(f => ({ ...f, spare_unit_id: '', _meta: { ...f._meta, spareUnitsLoading: true, spareUnits: [] } }));
      client.get('/inventory/spare-units/', { params: { warehouse: transitionFields.spare_warehouse, status: 'available' } })
        .then(({ data }) => setTransitionFields(f => ({ ...f, _meta: { spareUnits: data.results || data, spareUnitsLoading: false } })))
        .catch(() => setTransitionFields(f => ({ ...f, _meta: { spareUnits: [], spareUnitsLoading: false } })));
    }
  }, [transitionModal, transitionFields.rma_from, transitionFields.spare_warehouse]);

  // When "From Another Room" is selected in RMA, fetch sibling devices
  useEffect(() => {
    if (transitionModal === 'RMA' && transitionFields.rma_from === 'From Another Room' && ticket?.device_detail) {
      const dev = ticket.device_detail;
      const locationName = dev.room?.location?.name;
      const oem = dev.oem;
      const modelPart = dev.model_part;
      if (locationName && oem && modelPart) {
        client.get('/inventory/', {
          params: { oem, model_part: modelPart, location_name: locationName, exclude: dev.id, status: 'active' },
        })
          .then(({ data }) => setSiblingDevices(data.results || data))
          .catch(() => setSiblingDevices([]));
      }
    } else {
      setSiblingDevices([]);
    }
  }, [transitionModal, transitionFields.rma_from, ticket?.device_detail]);

  // Fetch RMA child ticket when ticket is in RMA or later status
  useEffect(() => {
    const rmaStatuses = ['RMA', 'Replacement', 'Resolved', 'Recovery', 'Closed'];
    if (ticket?.id && rmaStatuses.includes(ticket.status)) {
      client.get('/inventory/rma-tickets/', { params: { parent_ticket: ticket.id } })
        .then(r => {
          const results = r.data.results || r.data;
          setRmaChild(results.length > 0 ? results[0] : null);
        })
        .catch(() => {});
    } else {
      setRmaChild(null);
    }
  }, [ticket?.id, ticket?.status]);

  // Load all users for @mention autocomplete
  useEffect(() => {
    client.get('/auth/users/').then(r => {
      setAllUsers(Array.isArray(r.data) ? r.data : (r.data.results || []));
    }).catch(() => {});
  }, []);

  // Load user list when L2 modal opens
  useEffect(() => {
    if (l2Modal && l2Users.length === 0) {
      setL2Users(allUsers.length > 0 ? allUsers : []);
      if (allUsers.length === 0) {
        client.get('/auth/users/').then(r => {
          const users = Array.isArray(r.data) ? r.data : (r.data.results || []);
          setL2Users(users);
        }).catch(() => {});
      }
    }
  }, [l2Modal]);

  const handleShareL2 = async () => {
    if (!l2Selected) return;
    setL2Saving(true);
    try {
      await client.post(`/tickets/${ticket.id}/share-l2sme/`, { user_id: parseInt(l2Selected) });
      dispatch(fetchTicket(ticket.id));
      setL2Modal(false);
      setL2Selected('');
    } catch {}
    setL2Saving(false);
  };

  if (!ticket) return (
    <div className="flex items-center justify-center h-64">
      <div className="flex flex-col items-center gap-3">
        <svg className="animate-spin w-8 h-8 text-magenta-500" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
        <p className="text-gray-400 text-sm">Loading ticket...</p>
      </div>
    </div>
  );

  const transitions = getTransitions(ticket);
  const isSR = ticket.sub_category === 'SERVICE_REQUEST';
  const isPM = ticket.sub_category === 'PREVENTIVE_MAINTENANCE';
  const isAM = ticket.sub_category === 'ASSET_MANAGEMENT';

  // Pre-fill defaults for certain transitions from device/ticket data
  const getDefaultValues = (transition) => {
    if (transition === 'Ticket Assignment') {
      return { assigned_to: ticket.assigned_to ? String(ticket.assigned_to) : '' };
    }
    if (transition === 'Fault Confirmation') {
      return { fault_type: ticket.fault_type || 'HARDWARE' };
    }
    if (transition === 'RMA') {
      const isSamsungDisplay = ticket.device_detail?.oem?.toLowerCase() === 'samsung';
      return isSamsungDisplay ? { rma_from: 'From OEM' } : {};
    }
    if (transition === 'Replacement') {
      // "From Spares" RMAs have a real spare_unit with a serial number.
      // "From Another Room" RMAs borrow a sibling device instead — there's
      // no spare_unit for that case, but the serial is recorded in the RMA's
      // part_description text (e.g. "... (spare borrowed from another room —
      // serial: X)") at creation time. Pull it back out here so this field
      // doesn't sit blank for something the user already told the system.
      const borrowedSerialMatch = rmaChild?.part_description?.match(/serial:\s*([^\s)]+)/i);
      return {
        replaced_part_number: ticket.device_detail?.model_part || '',
        replaced_serial_number: rmaChild?.spare_unit_detail?.serial_number || borrowedSerialMatch?.[1] || '',
      };
    }
    if (transition === 'Recovery') {
      const today = new Date();
      const pad = n => String(n).padStart(2, '0');
      const todayStr = `${today.getFullYear()}-${pad(today.getMonth()+1)}-${pad(today.getDate())}`;
      return {
        picked_serial_number: ticket.device_detail?.serial_number || '',
        faulty_part_actual_pickup: todayStr,
      };
    }
    if (transition === 'Closed' || transition === 'Resolved') {
      const now = new Date();
      const pad = n => String(n).padStart(2, '0');
      const local = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
      return { resolved_on: local };
    }
    return {};
  };

  // Maps modal key → actual new_status (when they differ)
  const TRANSITION_MODAL_TO_STATUS = {};
  const TRANSITION_DISPLAY = { 'Configuration and Installation': 'In Progress' };
  const STATUS_LABELS = { 'Configuration and Installation': 'In Progress' };

  const handleTransitionClick = (transition) => {
    const fieldDefs = TRANSITION_FIELDS[transition];
    if (fieldDefs && fieldDefs.length > 0) {
      const initial = {};
      fieldDefs.forEach(f => { initial[f.name] = f.type === 'checkbox' ? false : ''; });
      const defaults = getDefaultValues(transition);
      setTransitionFields({ ...initial, ...defaults });
      // Store which transition we're coming from so doTransition can validate
      setTransitionModal(transition);
    } else {
      doTransition(transition, {});
    }
  };

  // Build field defs with mandatory overrides applied for current modal
  const getModalFieldDefs = (transition) => {
    const defs = TRANSITION_FIELDS[transition] || [];
    const requiredNames = getRequiredFieldNames(ticket?.status, transition);
    if (requiredNames.size === 0) return defs;
    return defs.map(f => requiredNames.has(f.name) ? { ...f, required: true } : f);
  };

  const doTransition = async (newStatus, rawFields) => {
    // Validate mandatory fields before sending
    const fieldDefs = getModalFieldDefs(newStatus);
    const missing = fieldDefs.filter(f => {
      if (!f.required) return false;
      if (f.type === 'checkbox') return false;
      const val = rawFields[f.name];
      return val === undefined || val === null || val === '';
    });
    if (missing.length > 0) {
      setError(`Required: ${missing.map(f => f.label).join(', ')}`);
      return;
    }

    setActionLoading(true);
    setError(null);
    const { notes, _meta, spare_unit_id, pm_report, ...ticketFieldsData } = rawFields;

    if (newStatus === 'RMA' && rawFields.rma_from === 'From Spares') {
      if (!spare_unit_id) {
        setActionLoading(false);
        setError('Please select a spare unit from the warehouse before confirming RMA.');
        return;
      }
      try {
        const useResp = await client.post(`/inventory/spare-units/${spare_unit_id}/use/`, { ticket_id: ticket.id, oem_rma_number: rawFields.oem_rma_number || '' });
        if (useResp.data?.rma_child_ticket_error) {
          // Log for debugging; ticket transition will still proceed but RMA child was not created
          console.error('RMA child ticket error:', useResp.data.rma_child_ticket_error);
        }
      } catch (err) {
        setActionLoading(false);
        setError(err?.response?.data?.detail || 'Failed to deduct spare unit from warehouse. Please try again.');
        return;
      }
    }
    // Note: "From OEM" and "From Another Room" RMA child ticket creation
    // (including auto-assignment to the Commercial team for OEM RMAs) is
    // handled entirely server-side by the /transition/ call below — creating
    // it here too would race the backend and could leave it unassigned.
    const cleanFields = {};
    Object.entries(ticketFieldsData).forEach(([k, v]) => {
      if (v === 'true') cleanFields[k] = true;
      else if (v === 'false') cleanFields[k] = false;
      else if (v !== '') cleanFields[k] = v;
    });

    // If a file is attached, use FormData (multipart)
    if (pm_report instanceof File) {
      const fd = new FormData();
      fd.append('new_status', newStatus);
      if (notes) fd.append('notes', notes);
      fd.append('pm_report', pm_report);
      Object.entries(cleanFields).forEach(([k, v]) => fd.append(`fields[${k}]`, v));
      try {
        const { data } = await client.post(`/tickets/${ticket.id}/transition/`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        dispatch({ type: 'tickets/fetchTicket/fulfilled', payload: data });
        setTransitionModal(null);
      } catch (err) {
        setError(err.response?.data || 'Transition failed');
      }
      setActionLoading(false);
      return;
    }

    const result = await dispatch(transitionTicket({
      id: ticket.id,
      transition: newStatus,
      notes: notes || '',
      fields: cleanFields,
    }));
    setActionLoading(false);
    if (transitionTicket.rejected.match(result)) {
      setError(result.payload || 'Transition failed');
    } else {
      setTransitionModal(null);
    }
  };

  const handleReassign = async () => {
    if (!reassignAgent) return;
    setReassignLoading(true);
    try {
      await client.patch(`/tickets/${ticket.id}/`, { assigned_to: parseInt(reassignAgent) });
      dispatch(fetchTicket(id));
      setReassignOpen(false);
      setReassignAgent('');
    } catch {
      // ignore — keep modal open
    }
    setReassignLoading(false);
  };

  const openEntitlementEdit = () => {
    const f = ticket.fields || {};
    const dev = ticket.device_detail;
    const loc = dev?.room?.location;
    setEntitlementForm({
      start_date:        f.start_date        || loc?.contract_start_date || '',
      end_date:          f.end_date          || loc?.contract_end_date   || '',
      oem:               f.oem               || dev?.oem        || '',
      part_number:       f.part_number       || dev?.model_part || '',
      installation_date: f.installation_date || dev?.installation_date || '',
      device_location:   f.device_location   || dev?.room?.location?.city || '',
      building_name:     f.building_name     || dev?.room?.building_name  || '',
      room_name:         f.room_name         || dev?.room?.room_name      || '',
      room_class:        f.room_class        || (dev?.room?.location?.sla_class ? `Class ${dev.room.location.sla_class}` : ''),
    });
    setEntitlementEdit(true);
  };

  const saveEntitlement = async () => {
    setEntitlementSaving(true);
    try {
      await client.patch(`/tickets/${id}/`, { fields: entitlementForm });
      dispatch(fetchTicket(id));
      setEntitlementEdit(false);
    } catch {}
    finally { setEntitlementSaving(false); }
  };

  const refreshEntitlementFromDevice = async () => {
    setEntitlementRefreshing(true);
    try {
      await client.post(`/tickets/${id}/refresh-fields/`);
      dispatch(fetchTicket(id));
    } catch {}
    finally { setEntitlementRefreshing(false); }
  };

  const handleFaultTypeChange = async (newFaultType) => {
    if (newFaultType === ticket.fault_type) return;
    setFaultTypeSaving(true);
    try {
      await client.patch(`/tickets/${id}/`, { fault_type: newFaultType });
      dispatch(fetchTicket(id));
    } catch {}
    finally { setFaultTypeSaving(false); }
  };

  const openTroubleshootingEdit = () => {
    const f = ticket.fields || {};
    setTroubleshootingForm({
      next_action: f.next_action || '',
      l2_sme: f.l2_sme || '',
      raise_case_with_oem: !!f.raise_case_with_oem,
      oem_sr_number: f.oem_sr_number || '',
      sr_created_on: f.sr_created_on || '',
      reason_oem_case: f.reason_oem_case || '',
    });
    setTroubleshootingEdit(true);
  };

  const saveTroubleshooting = async () => {
    setTroubleshootingSaving(true);
    try {
      await client.patch(`/tickets/${id}/`, { fields: troubleshootingForm });
      dispatch(fetchTicket(id));
      setTroubleshootingEdit(false);
    } catch {}
    finally { setTroubleshootingSaving(false); }
  };

  const handleSignoff = async () => {
    setActionLoading(true);
    await dispatch(signoffTicket(ticket.id));
    setActionLoading(false);
  };

  const handleCommentTextChange = (e) => {
    const val = e.target.value;
    setCommentText(val);
    // Detect @mention: find last @ in text
    const cursor = e.target.selectionStart;
    const textUpToCursor = val.slice(0, cursor);
    const match = textUpToCursor.match(/@(\w*)$/);
    if (match) {
      setMentionQuery(match[1]);
      setMentionDropdown(true);
    } else {
      setMentionDropdown(false);
    }
  };

  const insertMention = (user) => {
    const textarea = commentTextareaRef.current;
    if (!textarea) return;
    const cursor = textarea.selectionStart;
    const textUpToCursor = commentText.slice(0, cursor);
    const match = textUpToCursor.match(/@(\w*)$/);
    if (!match) return;
    const start = cursor - match[0].length;
    const newText = commentText.slice(0, start) + `@${user.full_name} ` + commentText.slice(cursor);
    setCommentText(newText);
    setMentionDropdown(false);
    setTimeout(() => { textarea.focus(); textarea.setSelectionRange(start + user.full_name.length + 2, start + user.full_name.length + 2); }, 0);
  };

  const handlePostComment = async () => {
    if (!commentText.trim() && commentFiles.length === 0) return;
    await dispatch(postComment({ ticketId: ticket.id, message: commentText.trim(), files: commentFiles }));
    setCommentText('');
    setCommentFiles([]);
    setMentionDropdown(false);
  };

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="rounded-2xl p-6" style={{background: 'linear-gradient(to right, #F5F3FC, #EAE7F8)', border: '1px solid #D0CBF0'}}>
        <button onClick={() => navigate('/tickets')} className="text-sm text-magenta-500 hover:text-magenta-600 font-medium mb-3 flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          Back to Tickets
        </button>
        {(() => { return (
        <div className="flex flex-col gap-3">
          {/* Top row: ticket number + timer + buttons */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-black text-brand-500">{ticket.ticket_number}</h1>
              <StatusBadge status={ticket.status} />
              {!isSR && !isPM && user.role !== 'RE' && (
              <button
                disabled={isClosed}
                onClick={async () => {
                  if (isClosed) return;
                  await client.patch(`/tickets/${ticket.id}/`, { is_downtime: !ticket.is_downtime });
                  dispatch(fetchTicket(id));
                }}
                title={isClosed ? 'Ticket is closed' : 'Toggle downtime'}
                className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full shadow-sm transition-colors ${
                  isClosed
                    ? 'opacity-40 cursor-not-allowed bg-white/40 text-gray-400 border border-gray-200'
                    : ticket.is_downtime
                      ? 'bg-magenta-500 text-white hover:bg-magenta-600'
                      : 'bg-white/60 text-gray-400 border border-dashed border-gray-300 hover:border-magenta-300 hover:text-magenta-400'
                }`}
              >
                {ticket.is_downtime && !isClosed && !ticket.resolved_at && <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />}
                {ticket.is_downtime ? 'DOWNTIME' : '+ Mark as Downtime'}
              </button>
              )}
            </div>
            <div className="flex flex-col items-end gap-2 flex-shrink-0">
              {ticket.is_downtime && ticket.created_at && (
                <NBDTimer
                  faultReportedAt={ticket.created_at}
                  frozenAt={ticket.resolved_at || null}
                  isOnHold={ticket.is_on_hold}
                  holdStartedAt={ticket.hold_started_at}
                  totalHoldMinutes={ticket.total_hold_minutes}
                  serviceStart={ticket.device_detail?.room?.service_start_time || '09:00'}
                  serviceEnd={ticket.device_detail?.room?.service_end_time || '17:00'}
                  resolved={['Resolved', 'Closed'].includes(ticket.status)}
                  warningMinutes={slaConfig.warningMinutes}
                  breachMinutes={slaConfig.breachMinutes}
                />
              )}
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {(['ADMIN', 'RE', 'TEAM_LEAD'].includes(user.role)) && (
                  <button
                    disabled={isClosed}
                    onClick={() => { if (isClosed) return; setReassignAgent(ticket.assigned_to ? String(ticket.assigned_to) : ''); setReassignOpen(true); }}
                    className={`flex items-center gap-1.5 text-xs font-semibold rounded-xl px-3 py-2 transition-colors border ${isClosed ? 'opacity-40 cursor-not-allowed text-gray-400 bg-white/40 border-gray-200' : 'text-brand-500 hover:text-magenta-600 bg-white/70 hover:bg-white border-brand-200 hover:border-magenta-300'}`}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Reassign
                  </button>
                )}
                {user.role === 'ADMIN' && (
                  <button
                    onClick={async () => {
                      if (!window.confirm('Permanently delete this ticket? This cannot be undone.')) return;
                      try {
                        await client.delete(`/tickets/${ticket.id}/`);
                        navigate('/tickets');
                      } catch {}
                    }}
                    className="flex items-center gap-1.5 text-xs font-semibold rounded-xl px-3 py-2 transition-colors border text-red-500 bg-white/70 hover:bg-red-50 border-red-200 hover:border-red-400"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                    Delete
                  </button>
                )}
                {!isClosed && (() => {
                  let visibleTransitions = transitions;
                  if (ticket.status === 'Open') {
                    if (ticket.assigned_to) {
                      const nextAfterAssignment = isSR ? 'Configuration and Installation'
                        : isPM ? 'Maintenance'
                        : 'Troubleshooting';
                      const replaced = transitions.map(t => t === 'Ticket Assignment' ? nextAfterAssignment : t);
                      visibleTransitions = [...new Set(replaced)];
                    } else if (!isSR && !isPM) {
                      visibleTransitions = transitions.filter(t => t !== 'Troubleshooting');
                    }
                  }
                  return visibleTransitions.map(t => (
                    <TransitionButton key={t} transition={t} onClick={handleTransitionClick} disabled={actionLoading} />
                  ));
                })()}
                {isSR && ['Ticket Assignment', 'Configuration and Installation', 'Resolved'].includes(ticket.status) && (
                  <button
                    onClick={() => setL2Modal(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-brand-200 text-brand-600 hover:bg-brand-50 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                    {ticket.fields?.l2_sme ? 'Change L2 / SME' : 'Share with L2 / SME'}
                  </button>
                )}
                {error && (
                  <p className="text-red-500 text-xs mt-1 w-full text-right">{typeof error === 'object' ? JSON.stringify(error) : error}</p>
                )}
              </div>
            </div>
          </div>
          <div>
            <p className="text-gray-500 text-sm mt-2">
              Created {new Date(ticket.created_at).toLocaleString('en-IN')}
              {ticket.created_by_name && <span className="text-gray-400"> · by {ticket.created_by_name}</span>}
            </p>
            {/* Assigned To + BHEL Contact chips */}
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <div className="flex items-center gap-2 bg-white/70 border border-brand-100 rounded-xl px-3 py-1.5">
                <div className="w-6 h-6 rounded-full bg-magenta-100 flex items-center justify-center text-xs font-bold text-magenta-600 flex-shrink-0">
                  {ticket.assigned_to_name ? ticket.assigned_to_name.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase() : '?'}
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-brand-400 uppercase leading-none">Ticket Owner</p>
                  <p className="text-xs font-semibold text-brand-600 mt-0.5">{ticket.assigned_to_name || 'Unassigned'}</p>
                </div>
              </div>
              {ticket.device_detail?.room?.location?.spoc_name && (
                <div className="flex items-start gap-2 bg-white/70 border border-brand-100 rounded-xl px-3 py-1.5">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3.5 h-3.5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-brand-400 uppercase leading-none">BHEL Contact</p>
                    <p className="text-xs font-semibold text-brand-600 mt-0.5">
                      {ticket.device_detail.room.location.spoc_name}
                      {ticket.device_detail.room.location.spoc_phone && (
                        <a href={`tel:${ticket.device_detail.room.location.spoc_phone}`} className="text-magenta-500 ml-1.5 hover:underline">
                          {ticket.device_detail.room.location.spoc_phone}
                        </a>
                      )}
                      {ticket.device_detail.room.location.spoc_email && (
                        <a href={`mailto:${ticket.device_detail.room.location.spoc_email}`} className="text-brand-400 ml-1.5 hover:underline font-normal">
                          {ticket.device_detail.room.location.spoc_email}
                        </a>
                      )}
                    </p>
                    {ticket.device_detail.room.location.address && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {ticket.device_detail.room.location.address}
                        {ticket.device_detail.room.location.pincode && ` — ${ticket.device_detail.room.location.pincode}`}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
            {/* Subject + Description */}
            <div className="mt-4 pt-4 border-t border-brand-200/50">
              {ticket.status === 'Open' && !openEdit && (
                <button onClick={() => { setOpenForm({ subject: ticket.subject || '', description: ticket.description || '' }); setOpenEdit(true); }}
                  className="float-right flex items-center gap-1 text-xs font-semibold text-brand-400 hover:text-brand-600 px-2 py-1 rounded-lg hover:bg-white/60 transition-colors">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/></svg>
                  Edit
                </button>
              )}
              {openEdit ? (
                <div className="space-y-2">
                  <input className="w-full border border-brand-300 rounded-lg px-3 py-2 text-sm font-bold text-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-300"
                    value={openForm.subject} onChange={e => setOpenForm(f => ({ ...f, subject: e.target.value }))} placeholder="Subject" />
                  <textarea rows={3} className="w-full border border-brand-300 rounded-lg px-3 py-2 text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-300 resize-none"
                    value={openForm.description} onChange={e => setOpenForm(f => ({ ...f, description: e.target.value }))} placeholder="Description" />
                  <div className="flex gap-2">
                    <button onClick={saveOpenEdit} disabled={openSaving || !openForm.subject.trim()}
                      className="px-4 py-1.5 bg-brand-500 text-white rounded-lg text-xs font-semibold hover:bg-brand-400 disabled:opacity-50">
                      {openSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button onClick={() => setOpenEdit(false)} className="px-4 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold text-gray-500 hover:bg-gray-50">Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <h2 className="font-bold text-brand-600 text-base">{ticket.subject}</h2>
                  <p className="text-sm text-gray-500 leading-relaxed mt-1">{ticket.description || <span className="italic text-gray-300">No description provided</span>}</p>
                </>
              )}
              <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                <select
                  value={ticket.category || 'INCIDENT'}
                  disabled={isClosed}
                  onChange={async e => {
                    const cat = e.target.value;
                    const firstSub = cat === 'NON_INCIDENT' ? 'PREVENTIVE_MAINTENANCE' : 'INCIDENT_SUB';
                    await client.patch(`/tickets/${ticket.id}/`, { category: cat, sub_category: firstSub });
                    dispatch(fetchTicket(id));
                  }}
                  className={`text-brand-500 text-xs font-semibold px-2.5 py-1 rounded-full border border-brand-200 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-300 ${isClosed ? 'opacity-40 cursor-not-allowed bg-white/40' : 'bg-white/60 cursor-pointer hover:border-brand-400'}`}
                >
                  <option value="INCIDENT">Incident</option>
                  <option value="NON_INCIDENT">Non-Incident</option>
                </select>
                <span className="text-gray-300 text-xs">/</span>
                <select
                  value={ticket.sub_category || 'INCIDENT_SUB'}
                  disabled={isClosed}
                  onChange={async e => {
                    await client.patch(`/tickets/${ticket.id}/`, { sub_category: e.target.value });
                    dispatch(fetchTicket(id));
                  }}
                  className={`text-brand-500 text-xs font-semibold px-2.5 py-1 rounded-full border border-brand-200 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-300 ${isClosed ? 'opacity-40 cursor-not-allowed bg-white/40' : 'bg-white/60 cursor-pointer hover:border-brand-400'}`}
                >
                  {ticket.category === 'NON_INCIDENT'
                    ? <><option value="PREVENTIVE_MAINTENANCE">Preventive Maintenance</option><option value="QUARTERLY_REVIEW">Quarterly Review</option><option value="ASSET_MANAGEMENT">Asset Management</option></>
                    : <><option value="INCIDENT_SUB">Incident</option><option value="SERVICE_REQUEST">Service Request</option></>
                  }
                </select>
              </div>
            </div>
          </div>
        </div>
        ); })()}
      </div>

      {/* Tab Bar */}
      <div className="flex items-center gap-1 border-b border-brand-100">
        {[
          { id: 'details',     label: 'Details',     icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
          { id: 'discussion',  label: 'Discussion',  count: comments.length, icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' },
          { id: 'attachments', label: 'Attachments', count: attachments.length || null, icon: 'M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13' },
          { id: 'escalation',  label: 'Escalation',  count: escalations.length || null, icon: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z' },
          { id: 'timestamps',  label: 'Timestamps',  icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
          { id: 'history',     label: 'History',     count: (ticket.status_logs || []).length, icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px ${
              activeTab === tab.id
                ? 'border-magenta-500 text-magenta-600'
                : 'border-transparent text-gray-400 hover:text-brand-500 hover:border-brand-200'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
            </svg>
            {tab.label}
            {tab.count != null && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${activeTab === tab.id ? 'bg-magenta-100 text-magenta-600' : 'bg-gray-100 text-gray-500'}`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── DETAILS TAB ── */}
      {activeTab === 'details' && (
      <div className="space-y-4">
          {/* Device / Room Info */}
          <div className="card p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="section-title mb-0">{isPM ? 'Room Information' : 'Device Information'}</h3>
              {ticket.status === 'Open' && !isPM && !deviceEdit && (
                <button onClick={() => setDeviceEdit(true)}
                  className="flex items-center gap-1 text-xs font-semibold text-brand-400 hover:text-brand-600 px-2 py-1 rounded-lg hover:bg-brand-50 transition-colors">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/></svg>
                  Change Device
                </button>
              )}
            </div>
            {deviceEdit && (
              <div className="mb-4 p-3 bg-brand-50 rounded-xl border border-brand-100 space-y-2">
                <input className="w-full border border-brand-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                  placeholder="Search by device name or serial…" value={deviceSearch}
                  onChange={e => setDeviceSearch(e.target.value)} />
                <select size={5} className="w-full border border-brand-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                  value={selectedDeviceId} onChange={e => setSelectedDeviceId(e.target.value)}>
                  {deviceOptions
                    .filter(d => {
                      const q = deviceSearch.toLowerCase();
                      return !q || d.device_name?.toLowerCase().includes(q) || d.serial_number?.toLowerCase().includes(q);
                    })
                    .map(d => (
                      <option key={d.id} value={d.id}>
                        {d.device_name} — {d.serial_number} ({d.room?.room_name || '—'})
                      </option>
                    ))}
                </select>
                <div className="flex gap-2">
                  <button onClick={saveDeviceEdit} disabled={openSaving || !selectedDeviceId}
                    className="px-4 py-1.5 bg-brand-500 text-white rounded-lg text-xs font-semibold hover:bg-brand-400 disabled:opacity-50">
                    {openSaving ? 'Saving…' : 'Confirm'}
                  </button>
                  <button onClick={() => { setDeviceEdit(false); setDeviceSearch(''); setSelectedDeviceId(''); }}
                    className="px-4 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold text-gray-500 hover:bg-gray-50">Cancel</button>
                </div>
              </div>
            )}
            {isPM ? (
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">Room</p><p className="font-medium text-gray-800">{ticket.room_detail?.room_name || '—'}</p></div>
                <div><p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">Room Type</p><p className="font-medium text-gray-800">{ticket.room_detail?.room_type || '—'}</p></div>
                <div><p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">Location</p><p className="font-medium text-gray-800">{ticket.room_detail?.location?.name || '—'}</p></div>
                <div><p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">City</p><p className="font-medium text-gray-800">{ticket.room_detail?.location?.city || '—'}</p></div>
              </div>
            ) : ticket.device_detail ? (
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">Device Name</p><p className="font-medium text-gray-800">{ticket.device_detail.device_name || '—'}</p></div>
                <div><p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">Serial Number</p><p className="font-mono font-medium text-gray-800">{ticket.device_detail.serial_number || '—'}</p></div>
                <div><p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">OEM</p><p className="font-medium text-gray-800">{ticket.device_detail.oem || '—'}</p></div>
                <div><p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">Model</p><p className="font-medium text-gray-800">{ticket.device_detail.model_part || '—'}</p></div>
                <div><p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">Installation Date</p><p className="font-medium text-gray-800">{ticket.device_detail.installation_date || '—'}</p></div>
                <div><p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">Room ID</p><p className="font-mono font-medium text-gray-800">{ticket.device_detail.room?.room_id || '—'}</p></div>
                <div><p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">Room</p><p className="font-medium text-gray-800">{ticket.device_detail.room?.room_name || '—'}</p></div>
                <div><p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">Building Name</p><p className="font-medium text-gray-800">{ticket.device_detail.room?.building_name || '—'}</p></div>
                <div><p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">Solution Type</p><p className="font-medium text-gray-800">{ticket.device_detail.room?.solution_type || '—'}</p></div>
                <div><p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">Floor</p><p className="font-medium text-gray-800">{ticket.device_detail.room?.floor || '—'}</p></div>
                <div><p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">Unit</p><p className="font-medium text-gray-800">{ticket.device_detail.room?.unit || '—'}</p></div>
                <div><p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">Sub Unit</p><p className="font-medium text-gray-800">{ticket.device_detail.room?.sub_unit || '—'}</p></div>
                <div><p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">State</p><p className="font-medium text-gray-800">{ticket.device_detail.room?.location?.state || '—'}</p></div>
                <div><p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">Location</p><p className="font-medium text-gray-800">{ticket.device_detail.room?.location?.name || '—'}</p></div>
              </div>
            ) : <p className="text-gray-400 text-sm">No device linked</p>}
          </div>

          {/* OEM Support Contacts */}
          {!isPM && vendorContacts.length > 0 && (
            <div className="card p-5">
              <h3 className="section-title mb-3">OEM Support Contacts</h3>
              <div className="space-y-4">
                {vendorContacts.map((vc, i) => (
                  <div key={i} className={`${vendorContacts.length > 1 ? 'border border-brand-100 rounded-xl p-4' : ''}`}>
                    {vendorContacts.length > 1 && (
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs font-bold text-brand-400 uppercase tracking-wider">{vc.region}{vc.territory ? ` — ${vc.territory}` : ''}</span>
                        {vc.issue_type && <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-semibold">{vc.issue_type}</span>}
                      </div>
                    )}
                    <div className="grid grid-cols-1 gap-3 text-sm">
                      {/* Primary */}
                      {vc.primary_name && (
                        <div className="flex items-start gap-3 bg-brand-50/50 rounded-lg px-3 py-2">
                          <div className="w-6 h-6 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <svg className="w-3.5 h-3.5 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                          </div>
                          <div className="min-w-0">
                            <p className="text-[10px] font-bold text-brand-400 uppercase">Primary Contact</p>
                            <p className="font-semibold text-gray-800">{vc.primary_name}</p>
                            <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                              {vc.primary_phone && <a href={`tel:${vc.primary_phone}`} className="text-xs text-brand-500 hover:underline">{vc.primary_phone}</a>}
                              {vc.primary_email && <a href={`mailto:${vc.primary_email}`} className="text-xs text-gray-400 hover:text-brand-500 hover:underline truncate">{vc.primary_email}</a>}
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Escalation 1 */}
                      {vc.esc1_name && (
                        <div className="flex items-start gap-3 bg-amber-50/50 rounded-lg px-3 py-2">
                          <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <svg className="w-3.5 h-3.5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6"/></svg>
                          </div>
                          <div className="min-w-0">
                            <p className="text-[10px] font-bold text-amber-500 uppercase">Escalation L1</p>
                            <p className="font-semibold text-gray-800">{vc.esc1_name}</p>
                            <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                              {vc.esc1_phone && <a href={`tel:${vc.esc1_phone}`} className="text-xs text-brand-500 hover:underline">{vc.esc1_phone}</a>}
                              {vc.esc1_email && <a href={`mailto:${vc.esc1_email}`} className="text-xs text-gray-400 hover:text-brand-500 hover:underline truncate">{vc.esc1_email}</a>}
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Final escalation */}
                      {vc.final_name && (
                        <div className="flex items-start gap-3 bg-red-50/50 rounded-lg px-3 py-2">
                          <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <svg className="w-3.5 h-3.5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                          </div>
                          <div className="min-w-0">
                            <p className="text-[10px] font-bold text-red-400 uppercase">Final Escalation</p>
                            <p className="font-semibold text-gray-800">{vc.final_name}</p>
                            <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                              {vc.final_phone && <a href={`tel:${vc.final_phone}`} className="text-xs text-brand-500 hover:underline">{vc.final_phone}</a>}
                              {vc.final_email && <a href={`mailto:${vc.final_email}`} className="text-xs text-gray-400 hover:text-brand-500 hover:underline truncate">{vc.final_email}</a>}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    {(vc.warranty_years || vc.mode_of_replacement || vc.contract_terms) && (
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-brand-50">
                        {vc.warranty_years && <span className="text-xs bg-white border border-brand-100 text-brand-600 px-2 py-0.5 rounded-full font-semibold">{vc.warranty_years}yr warranty</span>}
                        {vc.mode_of_replacement && <span className="text-xs bg-white border border-brand-100 text-gray-500 px-2 py-0.5 rounded-full">{vc.mode_of_replacement}</span>}
                        {vc.contract_terms && <span className="text-xs bg-white border border-brand-100 text-gray-500 px-2 py-0.5 rounded-full">{vc.contract_terms}</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Entitlement — visible for Incident/SR, not PM */}
          {!isPM && (<div className="card p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="section-title mb-0">Entitlement Details</h3>
              {!isClosed && !entitlementEdit && (
                <div className="flex items-center gap-2">
                  {ticket.device_detail && (
                    <button onClick={refreshEntitlementFromDevice} disabled={entitlementRefreshing}
                      title="Re-pull OEM, part number, contract dates, and building name from the linked device"
                      className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-brand-500 px-3 py-1.5 rounded-lg hover:bg-brand-50 transition-colors disabled:opacity-50">
                      <svg className={`w-3.5 h-3.5 ${entitlementRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
                      {entitlementRefreshing ? 'Refreshing…' : 'Refresh from Device'}
                    </button>
                  )}
                  <button onClick={openEntitlementEdit} className="flex items-center gap-1.5 text-xs font-semibold text-brand-500 hover:text-brand-400 px-3 py-1.5 rounded-lg hover:bg-brand-50 transition-colors">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/></svg>
                    Edit
                  </button>
                </div>
              )}
            </div>
            {entitlementEdit ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  {ENTITLEMENT_FIELDS.map(f => (
                    <div key={f.name}>
                      <label className="block text-xs font-semibold text-gray-500 mb-1">{f.label}</label>
                      <input
                        type={f.type}
                        className="input-field text-sm"
                        value={entitlementForm[f.name] || ''}
                        onChange={e => setEntitlementForm(p => ({ ...p, [f.name]: e.target.value }))}
                      />
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 pt-1">
                  <button onClick={saveEntitlement} disabled={entitlementSaving}
                    className="px-4 py-2 bg-brand-500 text-white rounded-xl text-xs font-semibold hover:bg-brand-400 disabled:opacity-50">
                    {entitlementSaving ? 'Saving…' : 'Save'}
                  </button>
                  <button onClick={() => setEntitlementEdit(false)} className="px-4 py-2 border border-gray-200 rounded-xl text-xs font-semibold text-gray-500 hover:bg-gray-50">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 text-sm">
                {ENTITLEMENT_FIELDS.map(f => {
                  const dev = ticket.device_detail;
                  const loc = dev?.room?.location;
                  const DEVICE_FALLBACKS = {
                    oem:             dev?.oem,
                    part_number:     dev?.model_part,
                    device_location: loc?.city,
                    building_name:   dev?.room?.building_name,
                    room_name:       dev?.room?.room_name,
                    room_class:      loc?.sla_class ? `Class ${loc.sla_class}` : undefined,
                    start_date:      loc?.contract_start_date,
                    end_date:        loc?.contract_end_date,
                  };
                  const val = ticket.fields?.[f.name] || DEVICE_FALLBACKS[f.name];
                  const isInferred = !ticket.fields?.[f.name] && !!DEVICE_FALLBACKS[f.name];
                  return (
                    <div key={f.name}>
                      <p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">{f.label}</p>
                      <p className={val ? `font-medium text-gray-800${isInferred ? ' opacity-60' : ''}` : 'text-gray-300 italic text-xs'}>
                        {val || 'Not set'}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>)}

          {/* Troubleshooting — Incident only */}
          {!isSR && !['Open', 'Ticket Assignment'].includes(ticket.status) && (
            <div className="card p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="section-title mb-0">Troubleshooting Details</h3>
                {!isClosed && !troubleshootingEdit && (
                  <button onClick={openTroubleshootingEdit} className="flex items-center gap-1.5 text-xs font-semibold text-brand-500 hover:text-brand-400 px-3 py-1.5 rounded-lg hover:bg-brand-50 transition-colors">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/></svg>
                    Edit
                  </button>
                )}
              </div>
              {troubleshootingEdit ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    {TRANSITION_FIELDS['Troubleshooting'].map(f => {
                      if (f.showWhen && troubleshootingForm[f.showWhen.field] !== f.showWhen.value) return null;
                      const label = f.label.replace(/\{OEM\}/g, ticket?.device_detail?.oem || 'OEM');
                      return (
                        <div key={f.name} className={f.type === 'textarea' ? 'col-span-2' : ''}>
                          {f.type !== 'checkbox' && (
                            <label className="block text-xs font-semibold text-gray-500 mb-1">{label}</label>
                          )}
                          <FieldInput
                            field={{ ...f, label }}
                            value={troubleshootingForm[f.name]}
                            onChange={v => setTroubleshootingForm(p => ({ ...p, [f.name]: v }))}
                            transitionFields={troubleshootingForm}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button onClick={saveTroubleshooting} disabled={troubleshootingSaving}
                      className="px-4 py-2 bg-brand-500 text-white rounded-xl text-xs font-semibold hover:bg-brand-400 disabled:opacity-50">
                      {troubleshootingSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button onClick={() => setTroubleshootingEdit(false)} className="px-4 py-2 border border-gray-200 rounded-xl text-xs font-semibold text-gray-500 hover:bg-gray-50">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="col-span-2">
                    <p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">Next Action</p>
                    <p className={ticket.fields?.next_action ? 'text-gray-700 leading-relaxed' : 'text-gray-300 italic text-xs'}>{ticket.fields?.next_action || 'Not set'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">L2 / SME</p>
                    <p className={ticket.fields?.l2_sme ? 'font-medium text-gray-800' : 'text-gray-300 italic text-xs'}>{ticket.fields?.l2_sme || 'Not set'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">OEM SR Number</p>
                    <p className={ticket.fields?.oem_sr_number ? 'font-medium text-gray-800' : 'text-gray-300 italic text-xs'}>{ticket.fields?.oem_sr_number || 'Not set'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">SR Created On</p>
                    <p className={ticket.fields?.sr_created_on ? 'font-medium text-gray-800' : 'text-gray-300 italic text-xs'}>{ticket.fields?.sr_created_on || 'Not set'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">Reason for OEM Case</p>
                    <p className={ticket.fields?.reason_oem_case ? 'text-gray-700' : 'text-gray-300 italic text-xs'}>{ticket.fields?.reason_oem_case || 'Not set'}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Configuration & Implementation details — Service Request */}
          {isSR && (ticket.fields?.l2_sme || ticket.fields?.oem_sr_number) && (
            <div className="card p-5">
              <h3 className="section-title">Configuration & Implementation</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                {ticket.fields.l2_sme && <div className="col-span-2"><p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">L2 / SME</p><p className="font-medium text-gray-800">{ticket.fields.l2_sme}</p></div>}
                {ticket.fields.oem_sr_number && <div><p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">OEM SR Number</p><p className="font-medium text-gray-800">{ticket.fields.oem_sr_number}</p></div>}
                {ticket.fields.sr_created_on && <div><p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">OEM SR Date</p><p className="font-medium text-gray-800">{ticket.fields.sr_created_on}</p></div>}
              </div>
            </div>
          )}

          {/* Confirm Fault Type — Incident only, the stage right after Troubleshooting.
              Lets the user confirm or change Hardware/Software before the ticket
              proceeds — RMA is only offered afterward if it's confirmed as Hardware. */}
          {!isSR && ticket.status === 'Fault Confirmation' && (
            <div className="card p-5 border-2 border-amber-200 bg-amber-50/40">
              <h3 className="section-title flex items-center gap-2">
                <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                Confirm Fault Type
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                Was selected as <span className="font-semibold">{ticket.fault_type === 'SOFTWARE' ? 'Software' : 'Hardware'}</span> at
                creation. Confirm it's still correct, or change it before proceeding — this decides whether
                Raise RMA is offered on the next step.
              </p>
              <div className="flex items-center gap-3">
                {['HARDWARE', 'SOFTWARE'].map(ft => (
                  <button
                    key={ft}
                    onClick={() => handleFaultTypeChange(ft)}
                    disabled={faultTypeSaving}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold border-2 transition-colors disabled:opacity-50 ${
                      ticket.fault_type === ft
                        ? 'bg-brand-500 border-brand-500 text-white'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-brand-300'
                    }`}
                  >
                    {ft === 'HARDWARE' ? 'Hardware' : 'Software'}
                  </button>
                ))}
                {faultTypeSaving && <span className="text-xs text-gray-400">Saving…</span>}
              </div>
            </div>
          )}

          {/* RMA Panel — Incident only, shown once ticket reaches RMA state */}
          {!isSR && ['RMA', 'Replacement', 'Resolved', 'Recovery', 'Closed'].includes(ticket.status) && (
            <RMAPanel
              ticket={ticket}
              rmaChild={rmaChild}
              onUpdate={(updated) => setRmaChild(updated)}
            />
          )}

          {/* Replacement — Incident only */}
          {!isSR && ticket.fields?.replaced_serial_number && (
            <div className="card p-5">
              <h3 className="section-title">Replacement Details</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">Replacement Serial</p><p className="font-mono font-medium text-gray-800">{ticket.fields.replaced_serial_number}</p></div>
                {ticket.fields.replaced_part_number && <div><p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">Replaced Part #</p><p className="font-medium text-gray-800">{ticket.fields.replaced_part_number}</p></div>}
                {ticket.fields.replacement_completed_on && <div><p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">Completed On</p><p className="font-medium text-gray-800">{ticket.fields.replacement_completed_on}</p></div>}
                {ticket.fields.next_action_replacement && <div className="col-span-2"><p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">Next Action</p><p className="text-gray-700">{ticket.fields.next_action_replacement}</p></div>}
              </div>
            </div>
          )}

          {/* Resolution / Closed */}
          {ticket.fields?.resolution && (
            <div className="card p-5">
              <h3 className="section-title">Resolution</h3>
              <p className="text-sm text-gray-700 leading-relaxed">{ticket.fields.resolution}</p>
              {ticket.resolved_at && <p className="text-xs text-gray-400 mt-2">Closed at: {new Date(ticket.resolved_at).toLocaleString('en-IN')}</p>}
            </div>
          )}

          {/* Recovery */}
          {(ticket.fields?.faulty_part_actual_pickup || ticket.fields?.picked_serial_number) && (
            <div className="card p-5">
              <h3 className="section-title">Recovery Details</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                {ticket.fields.picked_serial_number && <div><p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">Faulty Part Serial</p><p className="font-mono font-medium text-gray-800">{ticket.fields.picked_serial_number}</p></div>}
                {ticket.fields.faulty_part_actual_pickup && <div><p className="text-xs font-semibold text-brand-400 uppercase mb-0.5">Actual Pickup Date</p><p className="font-medium text-gray-800">{ticket.fields.faulty_part_actual_pickup}</p></div>}
              </div>
            </div>
          )}

          {/* PM Report — Preventive Maintenance only */}
          {isPM && (
            <div className="card p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="section-title mb-0">Maintenance Report</h3>
                <button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('access_token');
                      const apiBase = process.env.REACT_APP_API_URL || 'https://bhelsupport.proactive.co.in/api';
                      const res = await fetch(`${apiBase}/tickets/${ticket.id}/pm-template/`, {
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      if (!res.ok) throw new Error('Failed');
                      const blob = await res.blob();
                      const cd = res.headers.get('Content-Disposition') || '';
                      const match = cd.match(/filename="([^"]+)"/);
                      const name = match ? match[1] : 'PM_Template.xlsx';
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url; a.download = name; a.click();
                      URL.revokeObjectURL(url);
                    } catch { alert('Could not download template.'); }
                  }}
                  className="flex items-center gap-1.5 text-xs font-medium text-brand-500 hover:text-magenta-500 border border-brand-200 rounded-lg px-3 py-1.5 bg-brand-50/60 hover:bg-brand-100/60 transition-colors"
                >
                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
                  Download PM Template (XLSX)
                </button>
              </div>
              {ticket.fields?.pm_report ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 rounded-xl border border-brand-100 bg-brand-50/40">
                    <svg className="w-8 h-8 text-brand-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                    <div className="flex-1 min-w-0">
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          const url = `${(process.env.REACT_APP_API_URL || 'https://bhelsupport.proactive.co.in/api').replace('/api','')}/media/${ticket.fields.pm_report}`;
                          downloadFile(url, ticket.fields.pm_report.split('/').pop());
                        }}
                        className="text-sm font-semibold text-brand-500 hover:text-magenta-500 flex items-center gap-1.5"
                      >
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
                        <span className="truncate">{ticket.fields.pm_report.split('/').pop()}</span>
                      </a>
                      {ticket.fields.pm_notes && <p className="text-xs text-gray-500 mt-0.5">{ticket.fields.pm_notes}</p>}
                    </div>
                  </div>
                  {ticket.fields.pm_summary && (
                    <div>
                      <p className="text-xs font-semibold text-brand-400 uppercase tracking-wide mb-2">Report Summary</p>
                      <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 border border-gray-100 rounded-xl px-4 py-3">
                        {ticket.fields.pm_summary}
                      </p>
                    </div>
                  )}
                </div>
              ) : ticket.status === 'Maintenance' && !isClosed ? (
                <div className="space-y-3">
                  <p className="text-sm text-gray-400 italic">No report uploaded yet. Upload the completed PM report below.</p>
                  <div className="flex items-center gap-3">
                    <input
                      type="file"
                      accept=".xlsx,.xls,.pdf,.doc,.docx"
                      onChange={e => setPmUploadFile(e.target.files?.[0] || null)}
                      className="block flex-1 text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-brand-50 file:text-brand-600 hover:file:bg-brand-100 cursor-pointer"
                    />
                    <button
                      disabled={!pmUploadFile || pmUploading}
                      onClick={async () => {
                        if (!pmUploadFile) return;
                        setPmUploading(true);
                        const fd = new FormData();
                        fd.append('fields[pm_report]', pmUploadFile);
                        try {
                          await client.patch(`/tickets/${ticket.id}/`, fd, {
                            headers: { 'Content-Type': 'multipart/form-data' },
                          });
                          dispatch(fetchTicket(id));
                          setPmUploadFile(null);
                        } catch { /* ignore */ }
                        setPmUploading(false);
                      }}
                      className="flex-shrink-0 flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      {pmUploading ? (
                        <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                      ) : (
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l4-4m0 0l4 4m-4-4v12"/></svg>
                      )}
                      Upload
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-300 italic">No report uploaded yet.</p>
              )}
            </div>
          )}

          {/* BHEL Signoff */}
          {ticket.status === 'Closed' && user.role === 'BHEL_COORDINATOR' && !ticket.bhel_signoff && (
            <div className="rounded-2xl p-5 flex items-center justify-between" style={{background: 'linear-gradient(to right, #ECFDF5, #D1FAE5)', border: '1px solid #A7F3D0'}}>
              <div>
                <p className="font-bold text-emerald-800">BHEL Sign-off Required</p>
                <p className="text-sm text-emerald-600 mt-0.5">Confirm resolution and sign off to close the ticket.</p>
              </div>
              <button
                onClick={handleSignoff}
                disabled={actionLoading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-150 shadow-sm hover:shadow-md disabled:opacity-50"
              >
                Sign Off
              </button>
            </div>
          )}
      </div>
      )} {/* end details tab */}

      {/* ── DISCUSSION TAB ── */}
      {activeTab === 'discussion' && (
        <div className="card p-6">
          <div className="space-y-5 mb-6 max-h-[60vh] overflow-y-auto pr-2">
            {comments.length === 0 && (
              <p className="text-sm text-gray-400 italic text-center py-10">No comments yet. Start the discussion.</p>
            )}
            {comments.map(c => {
              const roleColors = { ADMIN: 'bg-brand-100 text-brand-600', RE: 'bg-emerald-100 text-emerald-700', MANAGER: 'bg-violet-100 text-violet-700', BHEL_COORDINATOR: 'bg-amber-100 text-amber-700' };
              const roleLabel = { ADMIN: 'Admin', RE: 'RE', MANAGER: 'Manager', BHEL_COORDINATOR: 'BHEL' };
              const roleCls = roleColors[c.author_role] || 'bg-gray-100 text-gray-600';
              // Render message with @mentions highlighted
              const renderMessage = (msg) => {
                const parts = msg.split(/(@\w[\w\s]*)/g);
                return parts.map((part, i) =>
                  part.startsWith('@') ? <span key={i} className="text-brand-500 font-semibold">{part}</span> : part
                );
              };
              return (
                <div key={c.id} className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-magenta-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5">
                    {c.author_initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span className="font-semibold text-brand-600 text-sm">{c.author_name}</span>
                      <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${roleCls}`}>{roleLabel[c.author_role] || c.author_role}</span>
                      {c.edited_at && <span className="text-xs text-gray-400 italic">(edited)</span>}
                      <span className="text-xs text-gray-400 ml-auto flex-shrink-0">
                        {new Date(c.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {(c.author === user.id || user.role === 'ADMIN') && editingCommentId !== c.id && (
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button onClick={() => startEditComment(c)} className="text-xs text-gray-400 hover:text-brand-500 font-medium">Edit</button>
                          <button onClick={() => handleDeleteComment(c.id)} className="text-xs text-gray-400 hover:text-red-500 font-medium">Delete</button>
                        </div>
                      )}
                    </div>
                    {editingCommentId === c.id ? (
                      <div className="space-y-2">
                        <textarea
                          value={editText}
                          onChange={e => setEditText(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) saveEditComment(c.id); if (e.key === 'Escape') cancelEditComment(); }}
                          rows={3}
                          className="input-field resize-none w-full text-sm"
                          autoFocus
                        />
                        <div className="flex gap-2 justify-end">
                          <button onClick={cancelEditComment} className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100">Cancel</button>
                          <button onClick={() => saveEditComment(c.id)} disabled={editSaving || !editText.trim()}
                            className="px-3 py-1.5 text-xs rounded-lg bg-brand-500 hover:bg-brand-400 text-white font-semibold disabled:opacity-50">
                            {editSaving ? 'Saving…' : 'Save'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-brand-50 rounded-xl rounded-tl-sm px-4 py-3 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {renderMessage(c.message)}
                      </div>
                    )}
                    {c.attachments && c.attachments.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {c.attachments.map(att => {
                          const isImage = att.mime_type?.startsWith('image/');
                          const linkProps = isImage
                            ? { href: att.file_url, target: '_blank', rel: 'noopener noreferrer' }
                            : { href: '#', onClick: (e) => { e.preventDefault(); downloadFile(att.file_url, att.file_name); } };
                          return (
                            <a key={att.id} {...linkProps}
                              className="flex items-center gap-1.5 text-xs text-brand-600 bg-brand-50 border border-brand-100 rounded-lg px-2.5 py-1.5 hover:bg-brand-100 transition-colors">
                              {isImage
                                ? <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                                : <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/></svg>
                              }
                              {att.file_name}
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="border-t border-brand-50 pt-5">
            <div className="flex gap-3 items-start">
              <div className="w-8 h-8 rounded-full bg-magenta-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-1">
                {(user.full_name || 'U').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 relative">
                <textarea
                  ref={commentTextareaRef}
                  value={commentText}
                  onChange={handleCommentTextChange}
                  onKeyDown={e => {
                    if (mentionDropdown && (e.key === 'Escape')) { setMentionDropdown(false); return; }
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handlePostComment();
                  }}
                  placeholder="Write a note… Type @ to mention someone. Ctrl+Enter to post."
                  rows={3}
                  className="input-field resize-none w-full"
                />
                {/* @mention dropdown */}
                {mentionDropdown && (
                  <div className="absolute z-50 bottom-full mb-1 left-0 bg-white border border-brand-200 rounded-xl shadow-lg max-h-48 overflow-y-auto w-64">
                    {allUsers
                      .filter(u => u.full_name?.toLowerCase().includes(mentionQuery.toLowerCase()))
                      .slice(0, 8)
                      .map(u => (
                        <button key={u.id} type="button"
                          onMouseDown={e => { e.preventDefault(); insertMention(u); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-brand-50 flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-magenta-100 text-magenta-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {(u.full_name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                          </span>
                          <span className="font-medium text-gray-800">{u.full_name}</span>
                          <span className="text-xs text-gray-400 ml-auto">{u.role}</span>
                        </button>
                      ))}
                    {allUsers.filter(u => u.full_name?.toLowerCase().includes(mentionQuery.toLowerCase())).length === 0 && (
                      <p className="text-xs text-gray-400 px-3 py-2">No users found</p>
                    )}
                  </div>
                )}
                {/* Pending file chips */}
                {commentFiles.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {commentFiles.map((f, i) => (
                      <span key={i} className="flex items-center gap-1.5 text-xs bg-brand-50 border border-brand-100 text-brand-700 rounded-lg px-2.5 py-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/></svg>
                        {f.name}
                        <button type="button" onClick={() => setCommentFiles(prev => prev.filter((_, j) => j !== i))}
                          className="text-gray-400 hover:text-red-500 ml-0.5">×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between mt-3 pl-11">
              <label className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-brand-500 cursor-pointer font-medium">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/></svg>
                Attach file
                <input type="file" className="hidden" multiple onChange={e => {
                  const selected = e.target.files ? Array.from(e.target.files) : [];
                  e.target.value = '';
                  if (selected.length > 0) setCommentFiles(prev => [...prev, ...selected]);
                }} />
              </label>
              <button onClick={handlePostComment} disabled={commentPosting || (!commentText.trim() && commentFiles.length === 0)}
                className="btn-primary flex items-center gap-2 disabled:opacity-50">
                {commentPosting
                  ? <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                  : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/></svg>
                }
                Post
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ATTACHMENTS TAB ── */}
      {activeTab === 'attachments' && (
        <div className="space-y-4">
          {/* Upload area */}
          <div className="card p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="section-title">Attachments</h3>
              <label className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl cursor-pointer transition-colors ${attachUploading ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-brand-500 hover:bg-brand-400 text-white'}`}>
                {attachUploading ? (
                  <><svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>Uploading…</>
                ) : (
                  <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/></svg>Upload File</>
                )}
                <input type="file" className="hidden" disabled={attachUploading} onChange={handleAttachUpload} />
              </label>
            </div>
            {attachError && <p className="text-red-600 text-xs bg-red-50 border border-red-200 rounded-xl px-3 py-2 mb-3">{attachError}</p>}
            {attachments.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-300">
                <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/></svg>
                <p className="text-sm text-gray-400">No attachments yet</p>
                <p className="text-xs text-gray-300 mt-1">Upload files, screenshots, or documents related to this ticket</p>
              </div>
            ) : (
              <div className="divide-y divide-brand-50">
                {attachments.map(a => {
                  const isImage = a.mime_type?.startsWith('image/');
                  const isPdf = a.mime_type === 'application/pdf';
                  const icon = isImage
                    ? 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'
                    : isPdf
                    ? 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z'
                    : 'M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13';
                  const sizeStr = a.file_size > 1048576
                    ? `${(a.file_size / 1048576).toFixed(1)} MB`
                    : `${Math.round(a.file_size / 1024)} KB`;
                  return (
                    <div key={a.id} className="flex items-center gap-3 py-3">
                      <div className="w-9 h-9 rounded-xl bg-brand-50 flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-brand-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={icon} />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{a.file_name}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{sizeStr} · {a.uploaded_by_name || 'Unknown'} · {new Date(a.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <a href="#" onClick={(e) => { e.preventDefault(); downloadFile(a.file_url, a.file_name); }}
                          className="p-1.5 rounded-lg text-brand-400 hover:bg-brand-50 hover:text-brand-600 transition-colors" title="Download">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
                        </a>
                        {!isClosed && (
                          <button onClick={() => handleAttachDelete(a.id)}
                            className="p-1.5 rounded-lg text-red-300 hover:bg-red-50 hover:text-red-500 transition-colors" title="Delete">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── ESCALATION TAB ── */}
      {activeTab === 'escalation' && (
        <div className="space-y-4">
          <EscalationCountdown createdAt={ticket.created_at} status={ticket.status} />
          {escalations.length === 0 ? (
            <div className="card p-10 text-center">
              <p className="text-gray-400 text-sm italic">No escalation contacts configured for {oem || 'this OEM'}.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-4">{oem} · {escalations.length} contact{escalations.length !== 1 ? 's' : ''}</p>
              {escalations.map(e => {
                const levelColor = e.level === 'L1'
                  ? { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-400', badge: 'bg-emerald-100 text-emerald-700' }
                  : e.level === 'L2'
                  ? { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700',   dot: 'bg-amber-400',   badge: 'bg-amber-100 text-amber-700'   }
                  : { bg: 'bg-magenta-50', border: 'border-magenta-200', text: 'text-magenta-700', dot: 'bg-magenta-400', badge: 'bg-magenta-100 text-magenta-700' };
                return (
                  <div key={e.id} className={`card p-5 border ${levelColor.border}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 mt-1 ${levelColor.dot}`} />
                        <div>
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${levelColor.badge}`}>{e.level}</span>
                            <p className="font-semibold text-brand-600 text-sm">{e.contact_name}</p>
                          </div>
                          {e.designation && <p className="text-xs text-gray-500">{e.designation}</p>}
                          {e.notes && <p className="text-xs text-gray-400 mt-1.5 leading-relaxed">{e.notes}</p>}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 flex-shrink-0 items-end">
                        {e.phone && (
                          <a href={`tel:${e.phone}`} className="flex items-center gap-1.5 text-sm text-brand-500 hover:text-magenta-500 transition-colors font-medium">
                            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                            {e.phone}
                          </a>
                        )}
                        {e.email && (
                          <a href={`mailto:${e.email}`} className="flex items-center gap-1.5 text-sm text-brand-500 hover:text-magenta-500 transition-colors font-medium">
                            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                            {e.email}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── HISTORY TAB ── */}
      {activeTab === 'timestamps' && (() => {
        const STATE_ORDER = [
          { state: 'Open',                  label: 'Ticket Created' },
          { state: 'Logged',                label: 'Logged' },
          { state: 'Entitlement Confirmed', label: 'Entitlement Confirmed' },
          { state: 'Ticket Assignment',     label: 'Ticket Assigned' },
          { state: 'Troubleshooting',       label: 'Troubleshooting Started' },
          { state: 'Configuration and Installation', label: 'In Progress' },
          { state: 'Maintenance',            label: 'Maintenance' },
          { state: 'RMA',                   label: 'RMA Initiated' },
          { state: 'Replacement',           label: 'Replacement Started' },
          { state: 'Resolved',              label: 'Resolved' },
          { state: 'Recovery',              label: 'Recovery Started' },
          { state: 'Closed',                label: 'Closed' },
        ];
        const logs = ticket.status_logs || [];
        const getTs = (stateEntry) => {
          const match = logs.find(l => l.to_status === stateEntry.state);
          return match ? match.changed_at : null;
        };
        const fmt = (ts) => new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        return (
          <div className="card p-6">
            <h3 className="text-sm font-bold text-brand-500 mb-4">State Timestamps</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {STATE_ORDER.map(entry => {
                const ts = getTs(entry);
                return (
                  <div key={entry.state} className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${ts ? 'border-brand-100 bg-brand-50/40' : 'border-gray-100 bg-gray-50/40'}`}>
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${ts ? 'bg-emerald-400' : 'bg-gray-200'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{entry.label}</p>
                      {ts
                        ? <p className="text-sm font-medium text-brand-700 mt-0.5">{fmt(ts)}</p>
                        : <p className="text-sm text-gray-300 mt-0.5">—</p>
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {activeTab === 'history' && (
        <div className="card p-6">
          {(ticket.status_logs || []).length === 0 ? (
            <p className="text-sm text-gray-400 italic text-center py-10">No history yet.</p>
          ) : (
            <ol className="relative border-l-2 border-brand-100 space-y-0 ml-3">
              {[...(ticket.status_logs || [])].reverse().map((log, i) => {
                const isCreated    = log.event_type === 'created';
                const isReassign   = log.event_type === 'reassign';
                const isDowntime   = log.event_type === 'field_change' && log.meta?.is_downtime !== undefined;
                const isRma        = log.event_type === 'rma';
                const isTransition = !isCreated && !isReassign && !isDowntime && !isRma;
                const dotColor = isCreated ? 'bg-emerald-400' : isReassign ? 'bg-amber-400' : isDowntime ? 'bg-rose-400' : isRma ? 'bg-sky-500' : 'bg-magenta-500';
                const label = isCreated ? 'Ticket Created'
                  : isReassign ? `Reassigned → ${log.meta?.to || '—'}`
                  : log.transition_name;
                const fieldChanges = log.meta?.fields ? Object.entries(log.meta.fields) : [];
                return (
                  <li key={log.id} className="ml-5 pb-7 last:pb-0">
                    <span className={`absolute -left-[7px] flex items-center justify-center w-3.5 h-3.5 rounded-full ring-4 ring-white ${dotColor}`} />
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <p className="text-sm font-bold text-brand-600">{label}</p>
                        {isTransition && (
                          <p className="text-xs text-gray-400 mt-0.5">
                            <span className="font-medium text-gray-500">{STATUS_LABELS[log.from_status] || log.from_status || 'New'}</span>
                            <span className="mx-1.5">→</span>
                            <span className="font-medium text-brand-500">{STATUS_LABELS[log.to_status] || log.to_status}</span>
                          </p>
                        )}
                        {isReassign && log.meta?.from && (
                          <p className="text-xs text-gray-400 mt-0.5">{log.meta.from} → {log.meta.to}</p>
                        )}
                        {log.notes && (
                          <p className="text-sm text-gray-600 mt-1.5 bg-brand-50 rounded-xl px-3 py-2 leading-relaxed whitespace-pre-wrap">{log.notes}</p>
                        )}
                        {isDowntime && (
                          <p className="text-xs text-gray-500 mt-1.5 bg-rose-50 rounded-xl px-3 py-2">
                            {log.meta.is_downtime?.to ? 'Marked as downtime' : 'Downtime cleared'}
                            {log.meta.elapsed_minutes != null && (
                              <span className="text-gray-400"> · {log.meta.elapsed_minutes} min elapsed at the time</span>
                            )}
                          </p>
                        )}
                        {(isRma || isCreated) && (log.meta?.details || []).length > 0 && (
                          <div className="mt-2 bg-sky-50 rounded-xl px-3 py-2 space-y-1">
                            {log.meta.details.map(([k, v], idx) => (
                              <p key={idx} className="text-xs text-gray-600">
                                <span className="font-semibold text-sky-700 uppercase">{k}</span>
                                <span className="ml-2">{v}</span>
                              </p>
                            ))}
                          </div>
                        )}
                        {fieldChanges.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {fieldChanges.map(([k, v]) => (
                              <p key={k} className="text-xs text-gray-500">
                                <span className="font-semibold text-brand-400 uppercase">{k.replace(/_/g, ' ')}</span>
                                {v.from ? <span className="text-gray-400 ml-1 line-through">{v.from}</span> : null}
                                <span className="text-brand-600 ml-1">{v.to}</span>
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs text-gray-500 font-medium">{log.changed_by_name || '—'}</p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {new Date(log.changed_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}

      {/* Reassign Modal */}
      {reassignOpen && (
        <div className="fixed inset-0 bg-brand-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm border border-brand-100">
            <div className="px-6 py-5 border-b border-brand-50 flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-magenta-50 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-magenta-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-bold text-brand-500">Reassign Ticket</h3>
                <p className="text-gray-400 text-xs mt-0.5">Select a new engineer to handle this ticket.</p>
              </div>
            </div>
            <div className="px-6 py-5">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Assign To <span className="text-magenta-500">*</span>
              </label>
              <select
                value={reassignAgent}
                onChange={e => setReassignAgent(e.target.value)}
                className="input-field"
              >
                <option value="">— Select engineer —</option>
                {agents.map(a => (
                  <option key={a.id} value={a.id}>{a.full_name}</option>
                ))}
              </select>
            </div>
            <div className="px-6 pb-5 flex gap-3">
              <button
                onClick={() => { setReassignOpen(false); setReassignAgent(''); }}
                className="btn-secondary flex-1 justify-center"
              >
                Cancel
              </button>
              <button
                onClick={handleReassign}
                disabled={!reassignAgent || reassignLoading}
                className="btn-primary flex-1 justify-center flex disabled:opacity-50"
              >
                {reassignLoading ? (
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                ) : 'Reassign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transition Modal */}
      {transitionModal && TRANSITION_FIELDS[transitionModal] && (() => { const modalFieldDefs = getModalFieldDefs(transitionModal); return (
        <div className="fixed inset-0 bg-brand-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-brand-100 max-h-[90vh] flex flex-col">
            {/* Modal header */}
            <div className="px-6 py-5 border-b border-brand-50">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-magenta-50 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-magenta-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                </div>
                <div>
                  <h3 className="text-base font-bold text-brand-500">Transition: {TRANSITION_DISPLAY[transitionModal] || transitionModal}</h3>
                  <p className="text-gray-400 text-xs mt-0.5">Fill in the details below to proceed with this transition.</p>
                </div>
              </div>
            </div>
            {/* Scrollable fields */}
            <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
              {modalFieldDefs.map(field => {
                // Hide fields that depend on another field being true
                if (field.showWhen) {
                  const depVal = transitionFields[field.showWhen.field];
                  if (depVal !== field.showWhen.value) return null;
                }
                // Replace {OEM} placeholder with the real OEM name
                const label = field.label.replace(/\{OEM\}/g, oem || 'OEM');
                const fieldValue = field.type === 'checkbox'
                  ? transitionFields[field.name] === true
                  : (transitionFields[field.name] ?? '');
                return (
                  <div key={field.name}>
                    {field.type !== 'checkbox' && (
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        {label}
                        {field.required && <span className="text-magenta-500 ml-0.5">*</span>}
                      </label>
                    )}
                    <FieldInput
                      field={{ ...field, label, _deviceName: ticket?.device_detail?.device_name }}
                      value={fieldValue}
                      onChange={val => setTransitionFields(prev => ({ ...prev, [field.name]: val }))}
                      agents={agents}
                      allUsers={allUsers}
                      siblingDevices={siblingDevices}
                      transitionFields={transitionFields}
                    />
                  </div>
                );
              })}
            </div>
            {/* Error */}
            {error && (
              <div className="px-6 pb-3">
                <p className="text-red-600 text-xs bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                  {typeof error === 'object' ? JSON.stringify(error) : error}
                </p>
              </div>
            )}
            {/* Actions */}
            <div className="px-6 py-4 border-t border-brand-50 flex gap-3">
              <button onClick={() => { setTransitionModal(null); setError(null); }} className="btn-secondary flex-1 justify-center">
                Cancel
              </button>
              <button
                onClick={() => doTransition(TRANSITION_MODAL_TO_STATUS[transitionModal] || transitionModal, transitionFields)}
                disabled={actionLoading}
                className="btn-primary flex-1 justify-center flex"
              >
                {actionLoading ? (
                  <span className="flex items-center gap-2"><svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>Processing...</span>
                ) : `Confirm → ${TRANSITION_DISPLAY[transitionModal] || transitionModal}`}
              </button>
            </div>
          </div>
        </div>
      ); })()}

      {/* L2/SME Modal */}
      {l2Modal && (
        <div className="fixed inset-0 bg-brand-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm border border-brand-100">
            <div className="px-6 py-5 border-b border-brand-50 flex items-center justify-between">
              <h3 className="text-base font-bold text-brand-500">Share with L2 / SME</h3>
              <button onClick={() => setL2Modal(false)} className="text-gray-300 hover:text-gray-500">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">Select Engineer / Expert</label>
                <select
                  value={l2Selected}
                  onChange={e => setL2Selected(e.target.value)}
                  className="input-field"
                >
                  <option value="">— Select —</option>
                  {l2Users.map(u => (
                    <option key={u.id} value={u.id}>{u.full_name || u.email} ({u.role})</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-brand-50 flex justify-end gap-2">
              <button onClick={() => setL2Modal(false)} className="px-4 py-2 text-sm font-semibold text-gray-500 border border-gray-200 rounded-xl hover:bg-gray-50">Cancel</button>
              <button
                onClick={handleShareL2}
                disabled={!l2Selected || l2Saving}
                className="px-5 py-2 text-sm font-semibold bg-brand-500 text-white rounded-xl hover:bg-brand-400 disabled:opacity-50"
              >
                {l2Saving ? 'Sharing…' : 'Share Ticket'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
